import { useState } from 'react'
import { router } from '@inertiajs/react'
import ActionItemModal from './ActionItemModal'
import TopicModal from './TopicModal'
import type { User, Meeting } from '../../types'

interface QuickActionsToolbarProps {
  meeting: Meeting
  teamMembers: User[]
  actionItemMembers?: User[]
  canFacilitate: boolean
  isLastSection?: boolean
  currentUserId?: number
  variant?: 'default' | 'scorecard'
  elapsedSeconds?: number
  attendanceCount?: number
  meetingDateLabel?: string
}

export default function QuickActionsToolbar({ meeting, teamMembers, actionItemMembers, canFacilitate, currentUserId, variant = 'default', elapsedSeconds = 0, attendanceCount = 0, meetingDateLabel }: QuickActionsToolbarProps) {
  const [showActionItemModal, setShowActionItemModal] = useState(false)
  const [showTopicModal, setShowTopicModal] = useState(false)
  const [showEndConfirm, setShowEndConfirm] = useState(false)
  const [showNotes, setShowNotes] = useState(false)
  const [notes, setNotes] = useState('')

  const handleEndMeeting = () => {
    router.post(`/meetings/${meeting.id}/end_meeting`, {
      rating: null,
      had_healthy_conflict: null,
    })
  }

  return (
    <>
      <div className={`quick-actions-toolbar ${variant === 'scorecard' ? 'quick-actions-toolbar--scorecard' : 'sticky top-0 z-30 bg-pinnacle-red px-4 py-2 flex items-center justify-between'}`}>
        {variant === 'scorecard' && (
          <div className="scorecard-meeting-meta" aria-label="Meeting status">
            <span>Meetings</span><span className="scorecard-separator">|</span>
            <strong>{meetingDateLabel}</strong>
            <span className="scorecard-status">In Progress</span>
            <span className="scorecard-time" aria-label="Elapsed meeting time"><svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3 2" /></svg>{Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}</span>
            <span className="scorecard-separator">·</span>
            <span>{attendanceCount} IN ATTENDANCE</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowActionItemModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-transparent text-white border border-white/30 rounded-button text-sm font-sans hover:bg-white/10 transition-colors min-h-[44px]"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Action Item
          </button>

          <button
            onClick={() => setShowTopicModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-transparent text-white border border-white/30 rounded-button text-sm font-sans hover:bg-white/10 transition-colors min-h-[44px]"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Topic
          </button>

          {variant !== 'scorecard' && (
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-transparent text-white border border-white/30 rounded-button text-sm font-sans hover:bg-white/10 transition-colors min-h-[44px]"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Notes
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {canFacilitate && (
            <button
              onClick={() => setShowEndConfirm(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-white/90 text-pinnacle-red rounded-button text-sm font-semibold font-sans transition-colors min-h-[44px]"
            >
              End Meeting
            </button>
          )}
        </div>
      </div>

      <ActionItemModal
        isOpen={showActionItemModal}
        onClose={() => setShowActionItemModal(false)}
        meetingId={meeting.id}
        teamId={meeting.team?.id}
        teamMembers={actionItemMembers ?? teamMembers}
      />

      <TopicModal
        isOpen={showTopicModal}
        onClose={() => setShowTopicModal(false)}
        meetingId={meeting.id}
        teamMembers={teamMembers}
        currentUserId={currentUserId}
      />

      {showNotes && (
        <div className="fixed inset-y-0 right-0 z-40 w-96 bg-white shadow-xl border-l border-stone/20 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stone/10">
            <h3 className="text-base font-semibold text-text-primary">Meeting Notes</h3>
            <button
              onClick={() => setShowNotes(false)}
              className="text-text-muted hover:text-text-primary transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 p-6">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Capture meeting notes here..."
              className="w-full h-full rounded-lg border border-stone/20 px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red resize-none text-base"
            />
          </div>
        </div>
      )}

      {showEndConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm"
            onClick={() => setShowEndConfirm(false)}
          />
          <div className="relative bg-white rounded-card shadow-elevated w-full max-w-sm mx-4 p-6 text-center">
            <h3 className="text-lg font-semibold text-text-primary mb-2 font-sans">End Meeting?</h3>
            <p className="text-base text-text-muted mb-6">This will end the meeting for all participants.</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowEndConfirm(false)}
                className="px-4 py-2 text-base font-semibold text-text-primary hover:bg-stone/10 rounded-button transition-colors font-sans min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleEndMeeting}
                className="px-4 py-2 text-base font-semibold text-white bg-pinnacle-red hover:bg-pinnacle-red-dark rounded-button transition-colors font-sans min-h-[44px]"
              >
                End Meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
